import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronRight, Star, BookOpen, TrendingUp, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { Progress } from '@/components/ui/progress';

const API = `${window.location.origin}/api`;

const BadgeDisplay = ({ compact = false }) => {
  const { isDarkMode } = useTheme();
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchBadgeData();
  }, []);

  const fetchBadgeData = async () => {
    try {
      const response = await axios.get(`${API}/user/badge`, { withCredentials: true });
      setBadgeData(response.data);
    } catch (error) {
      console.error('Failed to fetch badge data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${compact ? 'h-12' : 'h-24'} ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'} rounded-lg`} />
    );
  }

  if (!badgeData) return null;

  const { current_badge, next_badge, completed_courses, average_score, courses_to_next_level, score_needed_for_next } = badgeData;

  // Compact version for header/dashboard
  if (compact) {
    return (
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
          isDarkMode 
            ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' 
            : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
        }`}
        title={`${current_badge.name} - ${current_badge.title}`}
        data-testid="badge-compact"
      >
        <div 
          className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold"
          style={{ backgroundColor: current_badge.color, color: current_badge.level >= 4 ? '#1a1a1a' : '#fff' }}
        >
          {current_badge.level}
        </div>
        <div className="text-left">
          <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {current_badge.name}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {current_badge.title}
          </p>
        </div>
      </button>
    );
  }

  // Calculate progress percentage to next level
  const progressToNext = next_badge ? Math.min(
    ((current_badge.level) / 5) * 100,
    100
  ) : 100;

  // Full version for profile page — horizontal layout
  return (
    <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} data-testid="badge-display">
      <div className="flex flex-col md:flex-row">
        {/* Badge Identity - Left */}
        <div 
          className="md:w-56 shrink-0 p-6 flex flex-col items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${current_badge.color}15, ${current_badge.color}30)`,
            borderRight: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.06)'
          }}
        >
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg mb-3"
            style={{ backgroundColor: current_badge.color, color: current_badge.level >= 4 ? '#1a1a1a' : '#fff' }}
            data-testid="badge-level-circle"
          >
            {current_badge.level}
          </div>
          <h3 className={`text-lg font-bold text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {current_badge.name}
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {current_badge.title}
          </p>
        </div>

        {/* Details - Right */}
        <div className="flex-1 p-6">
          {/* Description */}
          <p className={`text-sm italic mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            "{current_badge.description}"
          </p>

          {/* Stats Row */}
          <div className="flex gap-6 mb-5">
            <div className="flex items-center gap-2">
              <BookOpen className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{completed_courses}</span> courses completed
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Star className={`w-4 h-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{average_score}%</span> avg quiz score
              </span>
            </div>
          </div>

          {/* Next Level Progress */}
          {next_badge ? (
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`} data-testid="next-badge-progress">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Next: <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{next_badge.name}</span>
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                {courses_to_next_level > 0 && (
                  <span className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <ChevronRight className="w-3 h-3" />
                    Complete {courses_to_next_level} more course{courses_to_next_level > 1 ? 's' : ''}
                  </span>
                )}
                {score_needed_for_next > 0 && (
                  <span className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <ChevronRight className="w-3 h-3" />
                    Raise avg score by {score_needed_for_next}%
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                Maximum level achieved!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* All Levels Toggle */}
      <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`w-full flex items-center justify-center gap-2 text-sm py-3 transition-colors ${
            isDarkMode 
              ? 'text-amber-400 hover:bg-gray-800' 
              : 'text-amber-600 hover:bg-amber-50'
          }`}
          data-testid="view-all-levels-btn"
        >
          {showDetails ? (
            <>Hide All Levels <ChevronUp className="w-4 h-4" /></>
          ) : (
            <>View All Levels <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
        
        {showDetails && (
          <div className={`px-6 pb-5 grid grid-cols-1 sm:grid-cols-5 gap-3`}>
            {badgeData.all_badges.map((badge) => {
              const isCurrent = badge.level === current_badge.level;
              const isPast = badge.level < current_badge.level;
              const isFuture = badge.level > current_badge.level;
              
              return (
                <div 
                  key={badge.level}
                  className={`relative flex flex-col items-center p-3 rounded-lg text-center transition-all ${
                    isCurrent
                      ? isDarkMode ? 'bg-amber-900/30 border-2 border-amber-600 ring-2 ring-amber-600/20' : 'bg-amber-50 border-2 border-amber-400 ring-2 ring-amber-400/20'
                      : isPast
                      ? isDarkMode ? 'bg-green-900/15 border border-green-800' : 'bg-green-50/50 border border-green-200'
                      : isDarkMode ? 'bg-gray-800/50 border border-gray-700 opacity-50' : 'bg-gray-50 border border-gray-200 opacity-50'
                  }`}
                  data-testid={`badge-level-${badge.level}`}
                >
                  {isPast && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold mb-2 ${isFuture ? 'opacity-40' : ''}`}
                    style={{ backgroundColor: badge.color, color: badge.level >= 4 ? '#1a1a1a' : '#fff' }}
                  >
                    {badge.level}
                  </div>
                  <p className={`text-xs font-semibold ${
                    isFuture ? isDarkMode ? 'text-gray-500' : 'text-gray-400' : isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {badge.name}
                  </p>
                  <p className={`text-[10px] leading-tight mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {badge.min_courses}+ courses, {badge.min_avg_score}%+
                  </p>
                  {isCurrent && (
                    <span className="mt-1.5 text-[10px] px-2 py-0.5 rounded-full bg-amber-500 text-white font-medium">Current</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeDisplay;
